import { useEffect, useState } from "react";
import { API } from "@/lib/api";

export const LANGS = [
  { code: "it", label: "Italiano" }, { code: "en", label: "English" }, { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" }, { code: "es", label: "Español" }, { code: "ru", label: "Русский" },
  { code: "ar", label: "العربية" }, { code: "zh", label: "中文" },
];

// Traduzione automatica del sito pubblico: il testo italiano originale resta la fonte,
// le traduzioni arrivano da /api/translate (AI) e vengono salvate in cache nel browser e sul server.
const state = { lang: localStorage.getItem("apollo_lang") || "it", listeners: new Set() };
const memory = {};
const textRec = new WeakMap();
const attrRec = new WeakMap();
const tracked = new Set();
const pending = new Set();
const failed = new Set();
const ATTRS = ["placeholder", "aria-label", "title"];
let timer = null;
let scheduled = false;

const dict = (lang) => {
  if (!memory[lang]) {
    try { memory[lang] = new Map(Object.entries(JSON.parse(localStorage.getItem(`apollo_tr_${lang}`) || "{}"))); }
    catch { memory[lang] = new Map(); }
  }
  return memory[lang];
};
const saveDict = (lang) => localStorage.setItem(`apollo_tr_${lang}`, JSON.stringify(Object.fromEntries(dict(lang))));
const translatable = (s) => /\p{L}{2,}/u.test(s);
const activePath = () => !/^\/(admin|sign-in|sign-up)/.test(window.location.pathname);
const skip = (el) => !el || el.closest("[translate='no'], script, style, noscript, textarea");

function apply(value, lang) {
  const core = value.trim();
  if (lang === "it" || !translatable(core)) return value;
  const tr = dict(lang).get(core);
  if (tr === undefined) {
    if (!failed.has(`${lang}:${core}`)) pending.add(core);
    return value; // nel frattempo mostra l'originale italiano, mai la lingua precedente
  }
  return value.replace(core, tr);
}

function processText(node, lang) {
  let rec = textRec.get(node);
  if (!rec || (node.nodeValue !== rec.shown && node.nodeValue !== rec.orig)) {
    rec = { orig: node.nodeValue, shown: node.nodeValue };
    textRec.set(node, rec);
    tracked.add(node);
  }
  const next = apply(rec.orig, lang);
  if (next !== null && next !== node.nodeValue) { rec.shown = next; node.nodeValue = next; }
}

function processAttrs(el, lang) {
  let rec = attrRec.get(el);
  if (!rec) { rec = {}; attrRec.set(el, rec); tracked.add(el); }
  ATTRS.forEach((a) => {
    const cur = el.getAttribute(a);
    if (cur == null) return;
    if (!rec[a] || (cur !== rec[a].shown && cur !== rec[a].orig)) rec[a] = { orig: cur, shown: cur };
    const next = apply(rec[a].orig, lang);
    if (next !== null && next !== cur) { rec[a].shown = next; el.setAttribute(a, next); }
  });
}

function run() {
  scheduled = false;
  const lang = activePath() ? state.lang : "it";
  // lingua e direzione solo sul sito pubblico; l'area admin resta italiana e LTR
  document.documentElement.lang = lang;
  document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  if (!activePath() && state.lang !== "it") return;
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (n.nodeType === 3) { if (n.nodeValue.trim() && !skip(n.parentElement)) processText(n, lang); }
    else if (ATTRS.some((a) => n.hasAttribute(a)) && !skip(n)) processAttrs(n, lang);
  }
  if (pending.size) { clearTimeout(timer); timer = setTimeout(() => fetchPending(lang), 150); }
}

async function fetchBatch(lang, texts) {
  try {
    const res = await fetch(`${API}/translate`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ lang, texts }) });
    if (!res.ok) throw new Error("translate");
    const { translations } = await res.json();
    texts.forEach((t, j) => dict(lang).set(t, translations[j] ?? t));
  } catch {
    texts.forEach((t) => failed.add(`${lang}:${t}`)); // niente nuovi tentativi in questa sessione
  }
}

async function fetchPending(lang) {
  const all = [...pending];
  pending.clear();
  const batches = [];
  for (let i = 0; i < all.length; i += 25) batches.push(all.slice(i, i + 25));
  await Promise.all(batches.map((b) => fetchBatch(lang, b)));
  saveDict(lang);
  if (lang === state.lang) schedule();
}

const schedule = () => {
  if (!scheduled) { scheduled = true; requestAnimationFrame(run); }
};

export const getLang = () => state.lang;

export function setLang(lang) {
  state.lang = lang;
  localStorage.setItem("apollo_lang", lang);
  state.listeners.forEach((l) => l(lang));
  schedule();
}

export function useLang() {
  const [lang, set] = useState(state.lang);
  useEffect(() => {
    state.listeners.add(set);
    return () => state.listeners.delete(set);
  }, []);
  return lang;
}

if (typeof window !== "undefined") {
  const start = () => {
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    setLang(state.lang);
  };
  if (document.body) start(); else window.addEventListener("DOMContentLoaded", start);
}
